import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef, effect } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { MatMenuTrigger } from '@angular/material/menu';
import { SnackbarService } from '../snackbar/snackbar.service';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject, takeUntil, filter, tap, catchError, EMPTY } from 'rxjs';
import { NotificationService } from '../../services/notification.service';
import { Notification, NotificationListResponse, ConnectionState, NotificationSettings } from '../../models/notification.model';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../services/user.service';
import { UserLocaleService } from '../../services/user-locale.service';
import { NotificationDetailModalComponent } from './notification-detail-modal/notification-detail-modal.component';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { CreateTaskModalComponent } from 'src/app/pages/my-tasks/components/create-task-modal/create-task-modal.component';
import { TaskFormValues } from 'src/app/pages/my-tasks/components/create-task-modal/create-task-modal.component';
import { UserRoleService } from 'src/app/core/services/role.service';
import { environment } from 'src/environments/environment';
import { DateFormatLocalizationService } from 'src/app/core/services/date-format-localization.service';

const CONFIG = {
  NOTIFICATIONS_PAGE_ROUTE: '/notifications',
  STORAGE_KEY_PREFIX: 'notificationFilter:',
  PAGE_SIZE: 20,
  MODAL_CLASS: 'download-budget-dialog modal-dialog-centered',
  MAX_PINNED_DISPLAY: 10,
} as const;
import { trigger, transition, style, animate } from '@angular/animations';

@Component({
  selector: 'app-notification-bell',
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.scss'],
  animations: [
    trigger('toastAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-20px)' }))
      ])
    ])
  ]
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;
  @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLDivElement>;

  unreadCount$: Observable<number>;
  notifications$: Observable<Notification[]>;
  activeTab: 'tareas' | 'recordatorios' = 'tareas';
  showOnlyUnread = false;
  isOnNotificationsPage = false;
  isLoadingMore = false;
  activeNotifications: Notification[] = [];
  postponedNotifications: Notification[] = [];
  pinnedNotifications: Notification[] = [];
  unpinnedNotifications: Notification[] = [];

  connectionState: ConnectionState = 'disconnected';
  hasNewNotification = false;
  toastNotification: Notification | null = null;
  settings: NotificationSettings | null = null;

  private readonly destroy$ = new Subject<void>();
  private userEmail: string | null = null;
  private currentPage = 1;
  private totalPages = 1;
  private isScrollLoading = false;
  private toastTimeout?: ReturnType<typeof setTimeout>;
  private shakeTimeout?: ReturnType<typeof setTimeout>;
  private autoRefreshStarted = false;
  private currentRole: string = 'none';

  constructor(
    private notificationService: NotificationService,
    private router: Router,
    private translate: TranslateService,
    private userService: UserService,
    private localeService: UserLocaleService,
    private snackbarService: SnackbarService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private bsModalService: BsModalService,
    private userRoleService: UserRoleService,
    private dateFormatLocalization: DateFormatLocalizationService
  ) {
    this.unreadCount$ = this.notificationService.unreadCount$;
    this.notifications$ = this.notificationService.notifications$;

    effect(() => {
      this.localeService.currentLocale();
      const currentNotifications = this.notificationService.getCurrentNotifications();

      if (currentNotifications.length > 0) {
        const localizedNotifications = this.localizeNotifications(currentNotifications);
        this.categorizeNotifications(localizedNotifications);
      }
    });
  }

  ngOnInit(): void {
    this.setupRouterTracking();
    this.setupUserContext();
    this.setupNotificationsSync();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupRouterTracking(): void {
    const isOnNotificationsPath = () => this.router.url.startsWith(CONFIG.NOTIFICATIONS_PAGE_ROUTE);

    this.isOnNotificationsPage = isOnNotificationsPath();
    this.cdr.detectChanges();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        tap((event) => {
          this.isOnNotificationsPage = event.urlAfterRedirects.startsWith(CONFIG.NOTIFICATIONS_PAGE_ROUTE);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private setupUserContext(): void {
    this.userRoleService.getUserRole()
      .pipe(takeUntil(this.destroy$))
      .subscribe((role) => {
        const normalized = (role ?? 'none').toLowerCase();
        this.currentRole = normalized;
        if (normalized === 'none') {
          this.notificationService.stopAutoRefresh();
          this.autoRefreshStarted = false;
        } else if (this.userEmail) {
          this.startAutoRefreshOnce();
        }
      });

    this.userService.getUserInfo()
      .pipe(
        takeUntil(this.destroy$),
        tap(user => {
          if (user?.email) {
            this.userEmail = user.email;
            this.loadFilterState();
            if (this.currentRole !== 'none') {
              this.startAutoRefreshOnce();
            }
          } else {
            if (!environment.production) {
              console.debug('[NotificationBell] No user email found');
            }
            this.userEmail = null;
            this.notificationService.stopAutoRefresh();
            this.autoRefreshStarted = false;
          }
        })
      )
      .subscribe();

    this.notificationService.connectionState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.connectionState = state;
        this.cdr.detectChanges();
      });

    this.notificationService.newNotification$
      .pipe(takeUntil(this.destroy$))
      .subscribe((notification) => {
        this.handleNewNotification(notification);
      });

    this.notificationService.settings$
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        this.settings = settings;
      });
  }

  private setupNotificationsSync(): void {
    this.notifications$
      .pipe(
        takeUntil(this.destroy$),
        tap((notifications) => {
          const localizedNotifications = this.localizeNotifications(notifications);
          this.categorizeNotifications(localizedNotifications);
        })
      )
      .subscribe();
  }

  private startAutoRefreshOnce(): void {
    if (this.autoRefreshStarted) return;
    this.autoRefreshStarted = true;
    this.notificationService.startAutoRefresh();
  }

  private categorizeNotifications(notifications: Notification[]): void {
    this.pinnedNotifications = notifications.filter(n => n.isPinned && !n.isPostponed);
    this.postponedNotifications = notifications.filter(n => n.isPostponed);

    const unpinned = notifications.filter(n => !n.isPinned && !n.isPostponed);

    if (this.pinnedNotifications.length > 0) {
      this.activeNotifications = [];
      this.unpinnedNotifications = unpinned;
    } else {
      this.activeNotifications = unpinned;
      this.unpinnedNotifications = [];
    }

    this.cdr.markForCheck();
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }
    if (this.shakeTimeout) {
      clearTimeout(this.shakeTimeout);
    }
  }

  private handleNewNotification(notification: Notification): void {
    this.hasNewNotification = true;
    this.cdr.detectChanges();

    if (this.shakeTimeout) {
      clearTimeout(this.shakeTimeout);
    }
    this.shakeTimeout = setTimeout(() => {
      this.hasNewNotification = false;
      this.cdr.detectChanges();
    }, 1000);

    if (this.settings?.toastEnabled) {
      this.showToast(notification);
    }
  }

  private showToast(notification: Notification): void {
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }

    this.toastNotification = notification;
    this.cdr.detectChanges();

    const duration = this.settings?.toastDuration ?? 5000;
    this.toastTimeout = setTimeout(() => {
      this.dismissToast();
    }, duration);
  }

  dismissToast(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.toastNotification = null;
    this.cdr.detectChanges();
  }

  onToastClick(): void {
    if (this.toastNotification) {
      this.openNotificationDetail(this.toastNotification);
      this.dismissToast();
    }
  }

  getConnectionTooltip(): string {
    switch (this.connectionState) {
      case 'connected':
        return this.translate.instant('notifications.connection.connected');
      case 'connecting':
        return this.translate.instant('notifications.connection.connecting');
      case 'reconnecting':
        return this.translate.instant('notifications.connection.reconnecting');
      case 'disconnected':
        return this.translate.instant('notifications.connection.disconnected');
      case 'failed':
        return this.translate.instant('notifications.connection.failed');
      default:
        return '';
    }
  }

  onMenuOpened(): void {
    this.loadNotifications();
  }

  setActiveTab(tab: 'tareas' | 'recordatorios'): void {
    this.activeTab = tab;
  }

  openNotificationDetail(notification: Notification): void {
    const dialogRef = this.dialog.open(NotificationDetailModalComponent, {
      width: '500px',
      maxWidth: '95vw',
      data: { notification },
      panelClass: 'notification-detail-modal',
      autoFocus: true,
      restoreFocus: true
    });

    dialogRef.afterClosed().pipe(
      takeUntil(this.destroy$),
      tap((result) => {
        if (result?.markedAsRead && this.showOnlyUnread) {
          this.loadNotifications();
        }
      })
    ).subscribe();
  }

  markAsRead(notification: Notification, event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    this.notificationService.markAsRead(notification.id).pipe(
      takeUntil(this.destroy$),
      catchError(() => {
        this.loadNotifications();
        return EMPTY;
      })
    ).subscribe();
  }

  markAsUnread(notification: Notification, event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    this.notificationService.markAsUnread(notification.id).pipe(
      takeUntil(this.destroy$),
      catchError(() => {
        this.loadNotifications();
        return EMPTY;
      })
    ).subscribe();
  }

  trackByNotificationId(_index: number, notification: Notification): string {
    return notification.id;
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = 100;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + threshold;

    if (atBottom && !this.isScrollLoading && this.currentPage < this.totalPages) {
      this.loadMoreNotifications();
    }
  }

  private loadMoreNotifications(): void {
    if (this.isScrollLoading || this.currentPage >= this.totalPages) {
      return;
    }

    this.isScrollLoading = true;
    this.isLoadingMore = true;
    this.currentPage++;

    const filters = this.showOnlyUnread ? { read: false } : undefined;
    this.notificationService.getNotifications(this.currentPage, CONFIG.PAGE_SIZE, filters, true).pipe(
      takeUntil(this.destroy$),
      tap((response: NotificationListResponse) => {
        this.totalPages = response.pagination.totalPages;
        this.isScrollLoading = false;
        this.isLoadingMore = false;
      }),
      catchError(() => {
        this.currentPage--;
        this.isScrollLoading = false;
        this.isLoadingMore = false;
        return EMPTY;
      })
    ).subscribe();
  }

  markAllAsRead(): void {
    this.notificationService.markAllAsRead().pipe(
      takeUntil(this.destroy$),
      tap(() => {
        if (this.showOnlyUnread) {
          this.loadNotifications();
        }
      })
    ).subscribe();
  }

  viewAll(): void {
    this.menuTrigger.closeMenu();
    this.router.navigate(['/notifications']);
  }

  onDisabledBellClick(): void {
    if (this.isOnNotificationsPage) {
      this.snackbarService.showInfo(
        this.translate.instant('notifications.bell.alreadyOnPage'),
        this.translate.instant('notifications.title'),
        this.translate.instant('notifications.detail.close'),
        3000
      );
    }
  }

  toggleUnreadFilter(): void {
    this.showOnlyUnread = !this.showOnlyUnread;
    this.saveFilterState();
    this.loadNotifications();
  }

  private loadNotifications(): void {
    this.currentPage = 1;
    const filters = this.showOnlyUnread ? { read: false } : undefined;
    this.notificationService.getNotifications(1, CONFIG.PAGE_SIZE, filters).pipe(
      takeUntil(this.destroy$),
      tap((response: NotificationListResponse) => {
        this.totalPages = response.pagination.totalPages;
      })
    ).subscribe();
  }

  private loadFilterState(): void {
    if (!this.userEmail) return;

    try {
      const key = `${CONFIG.STORAGE_KEY_PREFIX}${this.userEmail}`;
      const saved = localStorage.getItem(key);
      if (saved !== null) {
        this.showOnlyUnread = saved === 'true';
        this.cdr.detectChanges();
        if (this.showOnlyUnread) {
          this.loadNotifications();
        }
      }
    } catch (error) {
      console.warn('[NotificationBell] Failed to load filter state:', error);
    }
  }

  private saveFilterState(): void {
    if (!this.userEmail) return;

    try {
      const key = `${CONFIG.STORAGE_KEY_PREFIX}${this.userEmail}`;
      localStorage.setItem(key, String(this.showOnlyUnread));
    } catch (error) {
      console.warn('[NotificationBell] Failed to save filter state:', error);
    }
  }

  getRelativeTime(date: Date | string): string {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffMs = now.getTime() - notificationDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return this.translate.instant('notifications.time.justNow');
    } else if (diffMins < 60) {
      return this.translate.instant('notifications.time.minutesAgo', { minutes: diffMins });
    } else if (diffHours < 24) {
      return this.translate.instant('notifications.time.hoursAgo', { hours: diffHours });
    } else if (diffDays < 7) {
      return this.translate.instant('notifications.time.daysAgo', { days: diffDays });
    } else {
      return this.dateFormatLocalization.formatDate(notificationDate);
    }
  }

  postponeNotification(notification: Notification, days: number): void {
    this.notificationService.postponeNotification(notification.id, days).pipe(
      takeUntil(this.destroy$),
      catchError(() => {
        this.loadNotifications();
        return EMPTY;
      })
    ).subscribe();
  }

  cancelPostponement(notification: Notification, event: Event): void {
    event.stopPropagation();
    this.notificationService.cancelPostponement(notification.id).pipe(
      takeUntil(this.destroy$),
      catchError(() => {
        this.loadNotifications();
        return EMPTY;
      })
    ).subscribe();
  }

  togglePin(notification: Notification, event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    this.notificationService.togglePin(notification.id).pipe(
      takeUntil(this.destroy$),
      catchError((error) => {
        if (error?.status === 400 && error?.error?.includes?.('Maximum')) {
          this.snackbarService.showWarning(
            this.translate.instant('notifications.pinLimitReached'),
            this.translate.instant('notifications.title'),
            this.translate.instant('common.close'),
            3000
          );
        }
        this.loadNotifications();
        return EMPTY;
      })
    ).subscribe();
  }

  openCreateTask(notification?: Notification, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    this.menuTrigger?.closeMenu();
    this.showCreateTaskModal(notification);
  }

  private showCreateTaskModal(notification?: Notification): void {
    const initialValues = notification
      ? this.mapNotificationToTaskInitialValues(notification)
      : undefined;

    requestAnimationFrame(() => {
      const modalRef: BsModalRef = this.bsModalService.show(CreateTaskModalComponent, {
        class: CONFIG.MODAL_CLASS,
        ignoreBackdropClick: false,
        initialState: initialValues ? { initialValues } : {},
      });

      this.wireModalResult(modalRef);
    });
  }

  private mapNotificationToTaskInitialValues(notification: Notification): Partial<TaskFormValues> {
    return {
      name: notification.title && notification.title.trim() ? notification.title.trim() : undefined,
      description: notification.message && notification.message.trim() ? notification.message.trim() : undefined,
    };
  }

  private wireModalResult(modalRef: BsModalRef): void {
    const component = modalRef.content as CreateTaskModalComponent | undefined;
    if (!component) return;

    component.result
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(new Observable<void>(observer => {
          modalRef.onHidden?.subscribe(() => {
            observer.next(undefined);
            observer.complete();
          });
        }))
      )
      .subscribe((created) => {
        if (created) {
          this.snackbarService.showSuccess(
            this.translate.instant('myTasks.create_task.task_created_success'),
            this.translate.instant('common.success')
          );
        }
      });
  }

  private localizeNotifications(notifications: Notification[]): Notification[] {
    const locale = this.localeService.getCurrentLocale();
    return notifications.map((notification) => {
      const dispatchOrNotification = notification.dispatch || notification;

      if (dispatchOrNotification.translationsJson || dispatchOrNotification.renderedTitle) {
        const localizedTitle = this.notificationService.getLocalizedTitle(dispatchOrNotification, locale);
        const localizedMessage = this.notificationService.getLocalizedMessage(dispatchOrNotification, locale);
        return {
          ...notification,
          title: localizedTitle,
          message: localizedMessage
        };
      }
      return notification;
    });
  }
}
